/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The rules of the begrippenlijst — what a begrip is, which URL it lives at,
 * what the list and the term page are told about it — in ONE place.
 *
 * Until 2026-09-30 the glossary was 32 numbered pairs of translation keys
 * (`begrippen.term_N_word` / `_def`) with their count and grouping in code.
 * Every begrip now has a page of its own (`/<lang>/begrippen/<slug>`) and the
 * terms are records in `DATA_DIR/i18n_admin/begrippen.json`, maintained in the
 * admin under Content productie → Begrippen.
 *
 * Plain ESM JavaScript on purpose (the kennisbankIndex.js / articleSeo.js
 * pattern), because the consumers cannot share TypeScript:
 *   1. server/routes/begrippen.ts — the public and the admin API
 *   2. scripts/prerender.mjs — the baked list, the baked term pages and the
 *      hydration seeds
 *   3. src/lib/seoHead.js — the head, baked and at request time alike
 *   4. server/sitemap.ts, server/llmsTxt.ts, server/spaRenderer.ts (the 301s)
 *   5. the client pages and the admin editor
 *
 * Both payload mirrors (the API and the bake) call {@link begripIndexRows} and
 * {@link begripDetailPayload} rather than assembling the shape themselves, so
 * the hydrating client can never be handed a different object than the one
 * the page was baked from. Pinned in server/__tests__/begrippenMirrors.test.ts.
 *
 * No seed in here: the 32 original terms live in src/lib/begrippenSeed.js and
 * are reached through src/lib/begrippenSource.js, which only the server and
 * the bake import — this module is in the client bundle, that one is not.
 */

import { clampMetaDescription, fillTemplate } from './templateVars.js';
import { compareCodePoints, compareNames } from './nameOrder.js';

/** The route segment, shared by the list (`/begrippen`) and the term pages. */
export const BEGRIPPEN_SEGMENT = 'begrippen';

/** Group ids, in the order the list shows them. Labels: `begrippen.group_<id>`. */
export const BEGRIP_GROUPS = ['basics', 'money', 'tech'];

const GROUP_SET = new Set(BEGRIP_GROUPS);

/** Former slugs kept per begrip — plenty for a lifetime of renames. */
export const BEGRIP_FORMER_SLUGS_MAX = 20;

/** A slug is lowercase words joined by single hyphens — nothing to encode. */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** @param {unknown} value @returns {string} */
const str = (value) => (typeof value === 'string' ? value : '');

/**
 * The group a begrip is listed under. An unknown or missing value (a retired
 * id, a typo in a hand-edited file) reads as the first group rather than
 * dropping the term off the page. Never throws.
 *
 * @param {{ group?: unknown } | null | undefined} record
 * @returns {string}
 */
export function begripGroupOf(record) {
  const g = record && typeof record.group === 'string' ? record.group.trim() : '';
  return GROUP_SET.has(g) ? g : BEGRIP_GROUPS[0];
}

/**
 * i18n key of a group's title.
 *
 * @param {string} id
 * @returns {string}
 */
export function begripGroupLabelKey(id) {
  return `begrippen.group_${id}`;
}

/** @param {unknown} slug */
export function isValidBegripSlug(slug) {
  return typeof slug === 'string' && SLUG_RE.test(slug);
}

/**
 * A slug from free text: lowercase, diacritics stripped, everything that is
 * not a-z/0-9 collapsed to one hyphen. `Slim laden (slimme laadpaal)` →
 * `slim-laden-slimme-laadpaal`. The editor's "Auto" button and the server's
 * fallback for a record saved without a slug use this one function.
 *
 * @param {unknown} text
 * @returns {string}
 */
export function slugifyBegrip(text) {
  return str(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Effective URL slug of a begrip in a language. `slug` is the Dutch slug and
 * the fallback; `slugEn` overrides English only when non-empty — the same
 * contract as a kennisbank article.
 *
 * @param {any} record
 * @param {'nl'|'en'} lang
 * @returns {string}
 */
export function begripSlug(record, lang) {
  if (lang === 'en') {
    const en = str(record?.slugEn).trim();
    if (en) return en;
  }
  return str(record?.slug).trim();
}

/**
 * Both languages' slugs — what hreflang, the sitemap and the 301 need.
 *
 * @param {{ slug?: unknown, slugEn?: unknown } | null | undefined} record
 * @returns {{ nl: string, en: string }}
 */
export function begripSlugByLang(record) {
  return { nl: begripSlug(record, 'nl'), en: begripSlug(record, 'en') };
}

/**
 * @param {'nl'|'en'} lang
 * @param {string} slug  Effective slug for that language.
 */
export function begripPath(lang, slug) {
  return `/${lang}/${BEGRIPPEN_SEGMENT}/${slug}`;
}

/**
 * Does this begrip have a real English version? The term is the gate because
 * the term is what the fallback keys on: a record with an English term serves
 * its English object, one without serves the Dutch one wholesale. Same idea
 * as src/lib/articleEnglish.js, on this record's own field.
 *
 * @param {any} record
 * @returns {boolean}
 */
export function hasEnglishBegrip(record) {
  return str(record?.en?.term).trim() !== '';
}

/**
 * The languages whose URL is a genuine page: Dutch always, English when translated.
 *
 * @param {{ en?: { term?: unknown } | null } | null | undefined} record
 * @returns {Array<'nl' | 'en'>}
 */
export function begripLanguages(record) {
  return hasEnglishBegrip(record) ? ['nl', 'en'] : ['nl'];
}

/**
 * The translation actually served in a language: the requested one, or Dutch
 * for an untranslated begrip under /en — never empty strings.
 *
 * @param {any} record
 * @param {'nl'|'en'} lang
 * @returns {{ term?: string, definition?: string, blocks?: any[], seoTitle?: string, seoDescription?: string }}
 */
export function effectiveBegripTranslation(record, lang) {
  if (lang === 'en' && hasEnglishBegrip(record)) return record.en;
  return record?.nl ?? {};
}

/**
 * The language of the WORDS served at a URL, which on an untranslated begrip's
 * /en/ page is Dutch. Drives `inLanguage` and `og:locale`; `<html lang>` stays
 * on the URL's language, as it does for articles.
 *
 * @param {any} record
 * @param {'nl'|'en'} lang
 * @returns {'nl'|'en'}
 */
export function begripContentLanguage(record, lang) {
  return lang === 'en' && !hasEnglishBegrip(record) ? 'nl' : lang;
}

// ------------------------------------------------------------------ records

/**
 * One language of a normalized begrip, every field present. `blocks` is passed
 * through as stored: the article block schema, which the editor types
 * (src/pages/admin/begrippen/begripTypes.ts) and nothing in here reads.
 *
 * @typedef {object} BegripTranslationRecord
 * @property {string} term
 * @property {string} definition
 * @property {unknown[]} blocks
 * @property {string} seoTitle
 * @property {string} seoDescription
 */

/**
 * One begrip as {@link normalizeBegrip} returns it: the shape of an entry in
 * begrippen.json and of a seeded record (src/lib/begrippenSource.js).
 *
 * @typedef {object} BegripRecord
 * @property {string} id
 * @property {string} slug
 * @property {string} slugEn
 * @property {string} group
 * @property {number} order
 * @property {boolean} published
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string[]} [formerSlugs]
 * @property {BegripTranslationRecord} nl
 * @property {BegripTranslationRecord} en
 */

/**
 * One language of a stored begrip, every field present.
 *
 * @param {unknown} value
 * @returns {BegripTranslationRecord}
 */
const normalizeTranslation = (value) => {
  const t = /** @type {Record<string, unknown>} */ (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  );
  return {
    term: str(t.term),
    definition: str(t.definition),
    blocks: Array.isArray(t.blocks) ? t.blocks : [],
    // Optional per-begrip overrides of the SEO template (seo.begrip_title /
    // seo.begrip_description). Empty = the template applies.
    seoTitle: str(t.seoTitle),
    seoDescription: str(t.seoDescription),
  };
};

/**
 * One record in the shape every reader can rely on, or null for something
 * that is not a begrip at all (no id). Tolerant on purpose: a hand-edited file
 * with a missing field must not take the glossary down.
 *
 * @param {any} raw
 * @returns {BegripRecord | null}
 */
export function normalizeBegrip(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (raw.id === undefined || raw.id === null || String(raw.id).trim() === '') return null;
  const former = Array.isArray(raw.formerSlugs)
    ? raw.formerSlugs
        .filter((/** @type {unknown} */ s) => typeof s === 'string' && s.trim())
        .map((/** @type {string} */ s) => s.trim())
    : [];
  return {
    id: String(raw.id),
    slug: str(raw.slug).trim(),
    slugEn: str(raw.slugEn).trim(),
    group: begripGroupOf(raw),
    order: typeof raw.order === 'number' && Number.isFinite(raw.order) ? raw.order : 0,
    published: raw.published === true,
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
    ...(former.length ? { formerSlugs: former } : {}),
    nl: normalizeTranslation(raw.nl),
    en: normalizeTranslation(raw.en),
  };
}

/**
 * The contents of begrippen.json as records, or null when the value is not a
 * begrippen file at all — the caller then falls back to the seed
 * (src/lib/begrippenSource.js). An empty list is a valid file: an editor who
 * removed every term has a glossary without terms, not the seed back.
 *
 * @param {unknown} raw
 * @returns {BegripRecord[] | null}
 */
export function normalizeBegrippen(raw) {
  if (!Array.isArray(raw)) return null;
  return /** @type {BegripRecord[]} */ (raw.map(normalizeBegrip).filter(Boolean));
}

/**
 * What the list order reads of a begrip.
 *
 * @typedef {{ group?: unknown, order?: number, nl?: { term?: unknown } | null, id?: unknown }} BegripOrderFields
 */

/**
 * List order: by group, then the curated `order` inside the group, then the
 * Dutch term, then id — so every mirror produces the same sequence even for
 * two terms an editor left on the same position.
 *
 * @param {BegripOrderFields | null | undefined} a
 * @param {BegripOrderFields | null | undefined} b
 * @returns {number}
 */
export function compareBegrippen(a, b) {
  const g = BEGRIP_GROUPS.indexOf(begripGroupOf(a)) - BEGRIP_GROUPS.indexOf(begripGroupOf(b));
  if (g !== 0) return g;
  const o = (a?.order ?? 0) - (b?.order ?? 0);
  if (o !== 0) return o;
  const t = compareNames(str(a?.nl?.term), str(b?.nl?.term));
  if (t !== 0) return t;
  return compareCodePoints(a?.id, b?.id);
}

/**
 * The begrippen the site shows, in list order. A begrip needs to be published,
 * to have a Dutch term and to have a slug: without the last two it has neither
 * a name nor an address.
 *
 * @param {any[]} records
 * @returns {any[]}
 */
export function publishedBegrippen(records) {
  return (Array.isArray(records) ? records : [])
    .filter(
      (r) => r?.published === true && str(r?.nl?.term).trim() !== '' && begripSlug(r, 'nl') !== ''
    )
    .sort(compareBegrippen);
}

/**
 * The position a new (or moved) begrip gets: after the last one of its group.
 *
 * @param {readonly { group?: unknown, order?: unknown }[] | null | undefined} records
 * @param {string} group
 * @returns {number}
 */
export function nextBegripOrder(records, group) {
  const orders = (Array.isArray(records) ? records : [])
    .filter((r) => begripGroupOf(r) === group)
    .map((r) => (typeof r.order === 'number' && Number.isFinite(r.order) ? r.order : 0));
  return orders.length ? Math.max(...orders) + 1 : 0;
}

/**
 * The begrip a requested slug belongs to, and whether that slug is its
 * canonical address in this language.
 *
 * A non-canonical hit is a moved address: a former slug, a mis-cased link, or
 * the other language's slug — `/en/begrippen/commissie` and
 * `/nl/begrippen/commission` are what the language switch produces, since it
 * only swaps the prefix. The server answers those with a 301
 * (server/spaRenderer.ts), the API resolves them and the page replaces the
 * address bar; nothing renders at a non-canonical URL.
 *
 * @param {any[]} records
 * @param {string} slug
 * @param {'nl'|'en'} lang
 * @returns {{ record: any, canonical: boolean } | null}
 */
export function findBegripBySlug(records, slug, lang) {
  const wanted = str(slug).trim();
  if (!wanted) return null;
  const published = publishedBegrippen(records);
  const exact = published.find((r) => begripSlug(r, lang) === wanted);
  if (exact) return { record: exact, canonical: true };
  const lower = wanted.toLowerCase();
  const other = lang === 'nl' ? 'en' : 'nl';
  const moved =
    published.find((r) => begripSlug(r, lang) === lower) ??
    published.find((r) => begripSlug(r, other) === lower) ??
    published.find((r) => Array.isArray(r.formerSlugs) && r.formerSlugs.includes(lower));
  return moved ? { record: moved, canonical: false } : null;
}

// ----------------------------------------------------------------- payloads

/**
 * The rows of the list: GET /api/begrippen and the bake's seed for /begrippen.
 *
 * @param {any[]} records  Raw records (the published gate is applied here).
 * @param {'nl'|'en'} lang
 * @returns {Array<{ id: string, slug: string, group: string, term: string, definition: string, hasEnglish: boolean }>}
 */
export function begripIndexRows(records, lang) {
  return publishedBegrippen(records).map((r) => {
    const tr = effectiveBegripTranslation(r, lang);
    return {
      id: String(r.id),
      slug: begripSlug(r, lang),
      group: begripGroupOf(r),
      term: str(tr.term),
      definition: str(tr.definition),
      // Is the text above English, or the Dutch fallback? Same field and
      // meaning as on the article payloads.
      hasEnglish: hasEnglishBegrip(r),
    };
  });
}

/**
 * The payload of one term page: GET /api/begrippen/:slug and the bake's seed.
 * `related` is every other published begrip of the same group, in list order —
 * the internal links that make a term page more than its definition.
 *
 * @param {any} record
 * @param {'nl'|'en'} lang
 * @param {any[]} records  Every record, for `related`.
 */
export function begripDetailPayload(record, lang, records) {
  const tr = effectiveBegripTranslation(record, lang);
  const group = begripGroupOf(record);
  return {
    id: String(record.id),
    slug: begripSlug(record, lang),
    slugByLang: begripSlugByLang(record),
    hasEnglish: hasEnglishBegrip(record),
    group,
    term: str(tr.term),
    definition: str(tr.definition),
    blocks: Array.isArray(tr.blocks) ? tr.blocks : [],
    seoTitle: str(tr.seoTitle).trim(),
    seoDescription: str(tr.seoDescription).trim(),
    createdAt: str(record.createdAt),
    updatedAt: str(record.updatedAt),
    related: publishedBegrippen(records)
      .filter((r) => String(r.id) !== String(record.id) && begripGroupOf(r) === group)
      .map((r) => ({
        id: String(r.id),
        slug: begripSlug(r, lang),
        term: str(effectiveBegripTranslation(r, lang).term),
      })),
  };
}

// ---------------------------------------------------------------------- SEO

/**
 * Longest description an editor's own `seoDescription` may carry. Text the
 * page derives from the definition is capped at META_DESCRIPTION_MAX instead
 * (src/lib/templateVars.js): result pages cut there, and the definition runs
 * to 300 characters.
 */
const DESCRIPTION_MAX = 300;

/** @param {unknown} text @returns {string} */
const oneLine = (text) => str(text).replace(/\s+/g, ' ').trim();

/** What a term page is titled when `seo.begrip_title` is missing: the term alone. */
const FALLBACK_TITLE_TEMPLATE = '{term}';
const FALLBACK_DESCRIPTION_TEMPLATE = '{definition}';

/**
 * Title and description of a term page, before branding. One function for the
 * two head mirrors and the client page, so the three cannot disagree:
 *   1. the begrip's own `seoTitle` / `seoDescription` (the editor's override)
 *   2. the SEO tab's template row — `seo.begrip_title` / `seo.begrip_description`,
 *      with `{term}` and `{definition}` filled in
 *   3. the term and its definition
 *
 * @param {{ term?: string, definition?: string, seoTitle?: string, seoDescription?: string }} detail
 * @param {Record<string, unknown> | null | undefined} seo  The merged `seo` namespace of the page's language.
 * @returns {{ title: string, description: string }}
 */
export function begripSeoTexts(detail, seo) {
  const vars = { term: oneLine(detail?.term), definition: oneLine(detail?.definition) };
  /** @param {string} key @param {string} fallback */
  const template = (key, fallback) => {
    const value = seo && typeof seo === 'object' ? seo[key] : undefined;
    return typeof value === 'string' && value.trim() ? value : fallback;
  };
  const title =
    oneLine(detail?.seoTitle) ||
    oneLine(fillTemplate(template('begrip_title', FALLBACK_TITLE_TEMPLATE), vars)) ||
    vars.term;
  const own = oneLine(detail?.seoDescription);
  const description = own
    ? clampMetaDescription(own, DESCRIPTION_MAX)
    : clampMetaDescription(
        oneLine(
          fillTemplate(template('begrip_description', FALLBACK_DESCRIPTION_TEMPLATE), vars)
        ) || vars.definition
      );
  return { title, description };
}
